/**
 * Builders for context-menu surfaces. Each function returns a MenuItem[]
 * already wired to store actions for the given target.
 */

import {
  addLayer,
  bringForward,
  bringToFront,
  centerLayerH,
  centerLayerV,
  copyLayer,
  createGroupFromSelection,
  cutLayer,
  duplicateLayer,
  getState,
  hasClipboard,
  makeShapeLayer,
  makeTextLayer,
  pasteLayer,
  removeAllKeyframes,
  removeLayer,
  sendBackward,
  sendToBack,
  setEditingId,
  setLayerEndToPlayhead,
  setLayerStartToPlayhead,
  setPlayhead,
  setSelectedId,
  setTimelineQuantize,
  snapshotKeyframe,
  toggleLayerVisible,
  ungroup,
  updateLayer,
} from '../store/project';
import type { MenuItem } from './ContextMenu';

const MOD = navigator.platform.includes('Mac') ? '⌘' : 'Ctrl';

/** Used by both Canvas layer click and Layer panel row right-click. */
export function buildLayerMenu(layerId: string): MenuItem[] {
  const { project, playhead } = getState();
  const layer = project.layers.find((l) => l.id === layerId);
  if (!layer) return [];
  const idx = project.layers.findIndex((l) => l.id === layerId);
  const last = project.layers.length - 1;

  const isText = layer.kind === 'text';
  const kfActive = (layer.keyframes ?? []).some((k) => Math.abs(k.t - playhead) < 30);

  return [
    { label: '잘라내기', shortcut: `${MOD}X`, onClick: () => cutLayer(layerId) },
    { label: '복사', shortcut: `${MOD}C`, onClick: () => copyLayer(layerId) },
    { label: '붙여넣기', shortcut: `${MOD}V`, disabled: !hasClipboard(), onClick: () => pasteLayer() },
    { label: '복제', shortcut: `${MOD}D`, onClick: () => duplicateLayer(layerId) },
    {},
    isText
      ? { label: '텍스트 편집', shortcut: 'Enter', onClick: () => { setSelectedId(layerId); setEditingId(layerId); } }
      : { label: '이름 변경', onClick: () => promptRename(layerId, layer.name) },
    {},
    { label: '맨 앞으로', shortcut: `${MOD}⇧]`, disabled: idx === last, onClick: () => bringToFront(layerId) },
    { label: '한 단계 앞으로', shortcut: `${MOD}]`, disabled: idx === last, onClick: () => bringForward(layerId) },
    { label: '한 단계 뒤로', shortcut: `${MOD}[`, disabled: idx === 0, onClick: () => sendBackward(layerId) },
    { label: '맨 뒤로', shortcut: `${MOD}⇧[`, disabled: idx === 0, onClick: () => sendToBack(layerId) },
    {},
    { label: '가로 가운데 정렬', onClick: () => centerLayerH(layerId) },
    { label: '세로 가운데 정렬', onClick: () => centerLayerV(layerId) },
    {},
    {
      label: kfActive ? '◇ 현재 시간 키프레임 갱신' : '◇ 현재 시간에 키프레임 추가',
      onClick: () => snapshotKeyframe(layerId, playhead),
    },
    {
      label: '키프레임 모두 삭제',
      disabled: (layer.keyframes ?? []).length === 0,
      onClick: () => removeAllKeyframes(layerId),
    },
    {},
    {
      label: layer.visible ? '숨기기' : '표시',
      onClick: () => toggleLayerVisible(layerId),
    },
    {},
    layer.groupId
      ? { label: '그룹에서 빼기', onClick: () => ungroup(layer.groupId!) }
      : { label: '그룹으로 묶기', onClick: () => createGroupFromSelection() },
    {
      label: '삭제',
      shortcut: 'Del',
      danger: true,
      onClick: () => removeLayer(layerId),
    },
  ];
}

export function buildCanvasEmptyMenu(): MenuItem[] {
  return [
    { label: '텍스트 추가', onClick: () => addLayer(makeTextLayer()) },
    { label: '도형 추가', onClick: () => addLayer(makeShapeLayer()) },
    {},
    { label: '붙여넣기', shortcut: `${MOD}V`, disabled: !hasClipboard(), onClick: () => pasteLayer() },
    {},
    { label: '선택 해제', shortcut: 'Esc', onClick: () => setSelectedId(null) },
  ];
}

export function buildTimelineEmptyMenu(): MenuItem[] {
  const { isPlaying, playhead, project, timelineQuantize } = getState();
  return [
    {
      label: isPlaying ? '일시정지' : '재생',
      shortcut: 'Space',
      onClick: () => {
        // togglePlay lives in main.ts — fire a CustomEvent it listens to.
        window.dispatchEvent(new CustomEvent('promo-toggle-play'));
      },
    },
    {
      label: '재생 헤드 처음으로',
      disabled: playhead === 0,
      onClick: () => setPlayheadProxy(0),
    },
    {
      label: '재생 헤드 끝으로',
      disabled: playhead >= project.duration,
      onClick: () => setPlayheadProxy(project.duration),
    },
    {},
    {
      label: timelineQuantize ? '퀀타이즈 끄기' : '퀀타이즈 켜기',
      onClick: () => toggleQuantize(),
    },
    {},
    { label: '텍스트 추가', onClick: () => addLayer(makeTextLayer()) },
    { label: '도형 추가', onClick: () => addLayer(makeShapeLayer()) },
    {},
    { label: '붙여넣기', shortcut: `${MOD}V`, disabled: !hasClipboard(), onClick: () => pasteLayer() },
  ];
}

function setPlayheadProxy(t: number): void {
  setPlayhead(t);
}
function toggleQuantize(): void {
  setTimelineQuantize(!getState().timelineQuantize);
}

export function buildTimelineBarMenu(layerId: string): MenuItem[] {
  const { project, playhead } = getState();
  const layer = project.layers.find((l) => l.id === layerId);
  if (!layer) return [];
  const inSpan = playhead > layer.start && playhead < layer.end;

  return [
    {
      label: '◇ 현재 시간에 키프레임 추가',
      disabled: !inSpan,
      onClick: () => snapshotKeyframe(layerId, playhead),
    },
    {
      label: '키프레임 모두 삭제',
      disabled: (layer.keyframes ?? []).length === 0,
      onClick: () => removeAllKeyframes(layerId),
    },
    {},
    {
      label: '시작점을 현재 시간으로',
      disabled: playhead >= layer.end,
      onClick: () => setLayerStartToPlayhead(layerId),
    },
    {
      label: '끝점을 현재 시간으로',
      disabled: playhead <= layer.start,
      onClick: () => setLayerEndToPlayhead(layerId),
    },
    {},
    { label: '복제', shortcut: `${MOD}D`, onClick: () => duplicateLayer(layerId) },
    { label: '삭제', shortcut: 'Del', danger: true, onClick: () => removeLayer(layerId) },
  ];
}

function promptRename(layerId: string, current: string): void {
  const next = window.prompt('레이어 이름', current);
  if (next == null || next.trim() === '') return;
  updateLayer(layerId, { name: next.trim() });
}
